/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  type ReactElement,
  type RefObject,
  useEffect,
} from 'react';
import dynamic from 'next/dynamic';
import { Formik, Form, useFormikContext } from 'formik';
import { Button, SearchBar, Text } from '@az/starc-ui';
import cx from 'classnames';
import Title from '@/components/AZCustomComponent/Title';
import { validateSearch } from '@/components/StoreLocator';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { type ContentStackStoreLocator } from '@/features/contentstack/interface/content-types';
import { PlacesApiProvider } from '@/features/addressAutocomplete/components/PlacesApiProvider';
import { usePlacesAutocomplete } from '@/features/addressAutocomplete/hooks/usePlacesAutocomplete';
import { calculateSearchRadius } from '@/features/addressAutocomplete/utils/calculateSearchRadius';
import type { PlacePrediction } from '@/features/addressAutocomplete';
import { interpolateLabel } from '@/features/i18n/utils/interpolateLabel';
import CloseIcon from '@/public/images/close.svg';
import styles from './styles.module.scss';

type FormValues = {
  searchValue: string;
  predictions: PlacePrediction[];
};

const LazySearchStoreModal = dynamic(() =>
  import('../StoreModal/SearchStoreModal').then((module) => ({
    default: module.SearchStoreModal,
  })),
);

type SearchStoreInputProps = {
  value: string;
  onValueChange: (value: string) => void;
  onSelectPrediction: (prediction: PlacePrediction) => void;
  onSuggestionWithCoordinates: (
    prediction: PlacePrediction,
    coordinates: { latitude: number; longitude: number } | undefined,
    radius: number | undefined,
  ) => void;
  endElement?: ReactElement;
  inputContainerClassName?: string;
  label: string;
  searchStoreInputRef: RefObject<SearchStoreInputHandle | null>;
};

export type SearchStoreInputHandle = {
  handleFirstSuggestionSelect: () => Promise<void>;
};

function SearchStoreInput({
  value,
  onValueChange,
  onSelectPrediction,
  onSuggestionWithCoordinates,
  endElement,
  inputContainerClassName,
  label,
  searchStoreInputRef,
}: SearchStoreInputProps) {
  const { setFieldValue } = useFormikContext<FormValues>();
  const inputRef = useRef<HTMLInputElement>(null);
  const { predictions, consumeSession, getPlaceCoordinates } = usePlacesAutocomplete({
    query: value,
  });

  const suggestionStrings = useMemo(() => predictions.map((p) => p.fullText), [predictions]);

  useImperativeHandle(
    searchStoreInputRef,
    () => ({
      handleFirstSuggestionSelect: async () => {
        if (predictions[0]) {
          await handleSuggestionSelect(predictions[0].fullText ?? '');
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [predictions],
  );

  useEffect(() => {
    void setFieldValue('predictions', predictions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predictions]);

  const predictionByText = useMemo(
    () => new Map(predictions.map((p) => [p.fullText, p])),
    [predictions],
  );

  const fetchCoordinatesAndNotify = async (prediction: PlacePrediction) => {
    try {
      const response = await getPlaceCoordinates(prediction.placeId);

      if (response?.results?.[0]?.geometry?.location && response.results[0].geometry.bounds) {
        const location = response.results[0].geometry.location;
        const bounds = response.results[0].geometry.bounds;
        const coordinates = {
          latitude: location.lat(),
          longitude: location.lng(),
        };

        const types = new Set(prediction.types);

        const shouldCalculateRadius = !(
          types.has('route') ||
          types.has('locality') ||
          types.has('sublocality') ||
          types.has('postal_code')
        );

        const radius = shouldCalculateRadius ? calculateSearchRadius(bounds) : undefined;

        onSuggestionWithCoordinates(prediction, coordinates, radius);
      } else {
        onSuggestionWithCoordinates(prediction, undefined, undefined);
      }
    } catch {
      onSuggestionWithCoordinates(prediction, undefined, undefined);
    }
  };

  const handleSuggestionSelect = async (selected: string) => {
    const prediction = predictionByText.get(selected);
    if (!prediction) {
      return;
    }

    consumeSession();
    onSelectPrediction(prediction);
    await fetchCoordinatesAndNotify(prediction);
  };

  return (
    <SearchBar
      ref={inputRef}
      inputClassName={styles.searchBarInput}
      inputContainerClassName={cx(styles.searchBarInputContainer, inputContainerClassName)}
      onValueChange={onValueChange}
      hideSuggestionsHeader
      disableSuggestionsScrollIntoView
      suggestionsClassName={styles.suggestionsDropdown}
      onSelectionChange={handleSuggestionSelect}
      endElement={endElement}
      hideSuggestions={suggestionStrings.length === 0 || value.length === 0}
      suggestions={suggestionStrings}
      value={value}
      label={label}
    />
  );
}

const labelMap = {
  input: 'label_locations_storeLocator_fieldPlaceholder',
  lblRequired: 'label_StoreLocator_modal_Required',
  lblPlease_remove_invalid_characters: 'label_Please_remove_invalid_characters',
  lblZipCodeMustBe5Digits: 'error_RewardsSignUp_body_ZipCodeMustBe5Digits',
  lblZipCodeBrazil: 'error_Brazil_ZipCodeFormat',
  locationError: 'label_location_error',
  searchResultsSearch: 'label_Search_Results_Search',
  lblClearSearch: 'label_Footer_modal_ClearSearch',
  zipInvalid: 'mf_registration_error_zip_not_valid',
  tryDifferentSearch: 'label_locations_storeLocator_tryDifferentSearch',
};

export function SearchStore({
  content,
  make,
  redirectToStore,
  className,
  inputContainerClassName,
  isLocationsPage = false,
  onClickSearch,
  onClickSuggestion,
}: {
  content?: ContentStackStoreLocator;
  make?: string | null;
  redirectToStore?: boolean;
  className?: string;
  inputContainerClassName?: string;
  isLocationsPage?: boolean;
  onClickSearch?: (searchText: string) => void;
  onClickSuggestion?: (
    prediction: PlacePrediction,
    coordinates: { latitude: number; longitude: number },
    radius?: number,
  ) => void;
}) {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const [showStoreLocator, setShowStoreLocator] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchStoreInputRef = useRef<SearchStoreInputHandle>(null);

  const title = make
    ? interpolateLabel(content?.heading ?? '', { make })
    : (content?.heading ?? '');

  const validate = (values: { searchValue: string }) => {
    const error = validateSearch(values.searchValue, labels, locale, {
      isUserCurrentLocation: false,
      hasData: false,
    });
    if (error) {
      return { searchValue: error };
    }
    return {};
  };

  const handleClose = () => {
    setShowStoreLocator(false);
    setSearchValue('');
  };

  return (
    <div className={className}>
      <div className={styles.searchStore}>
        {title && <Title stylesClass={styles.title} firstText={title} underline variant="md" />}
        <PlacesApiProvider>
          <Formik<FormValues>
            initialValues={{ searchValue: '', predictions: [] }}
            validate={validate}
            onSubmit={(values, helpers) => {
              if (isLocationsPage) {
                if (values.predictions.length === 0) {
                  helpers.setFieldError('searchValue', labels.tryDifferentSearch);
                }
              } else {
                setSearchValue(values.searchValue);
                setShowStoreLocator(true);
              }
            }}
            validateOnChange={false}
            validateOnBlur={true}
          >
            {({ values, errors, resetForm, setFieldValue, setFieldError }) => {
              const searchButtons =
                values.searchValue.length > 0 ? (
                  <>
                    <Button
                      type="button"
                      onClick={() => resetForm()}
                      className={styles.clearButton}
                      variant="ghost"
                    >
                      <CloseIcon
                        className={styles.clearButtonImg}
                        aria-hidden="true"
                        focusable="false"
                      />
                      <span className="sr-only">{labels.lblClearSearch}</span>
                    </Button>
                    <div className={styles.searchButtonsDivider}>
                      <div className={styles.verticalDivider} />
                      <Button
                        className={styles.searchBtn}
                        variant="ghost"
                        onClick={async () => {
                          if (values.predictions.length === 0) {
                            setFieldError('searchValue', labels.tryDifferentSearch);
                          } else {
                            await searchStoreInputRef.current?.handleFirstSuggestionSelect();
                          }
                        }}
                      >
                        <Text variant="text-link" className={styles.searchBtnText}>
                          {labels.searchResultsSearch}
                        </Text>
                      </Button>
                    </div>
                  </>
                ) : undefined;

              return (
                <Form>
                  <SearchStoreInput
                    searchStoreInputRef={searchStoreInputRef}
                    value={values.searchValue}
                    onValueChange={(value) => {
                      void setFieldValue('searchValue', value);
                      setFieldError('searchValue', '');
                    }}
                    onSelectPrediction={(prediction) => {
                      void setFieldValue('searchValue', prediction.fullText);
                      setFieldError('searchValue', '');
                    }}
                    onSuggestionWithCoordinates={(prediction, coordinates, radius) => {
                      if (isLocationsPage) {
                        if (coordinates) {
                          onClickSuggestion?.(prediction, coordinates, radius);
                        } else {
                          onClickSearch?.(prediction.fullText);
                        }
                      } else {
                        setSearchValue(prediction.fullText);
                        setShowStoreLocator(true);
                      }
                    }}
                    endElement={searchButtons}
                    inputContainerClassName={inputContainerClassName}
                    label={labels.input}
                  />
                  {errors.searchValue && (
                    <p role="alert" className={styles.errorMessage}>
                      {errors.searchValue}
                    </p>
                  )}
                </Form>
              );
            }}
          </Formik>
        </PlacesApiProvider>

        {content?.subtitle && <p className={styles.description}>{content.subtitle}</p>}
      </div>

      {showStoreLocator && (
        <LazySearchStoreModal
          searchValue={searchValue}
          closeStoreModal={handleClose}
          redirectToStore={redirectToStore}
        />
      )}
    </div>
  );
}
